/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { InvitationImport, InvitationsPage, InviteeResult } from '../../models';
import { ObservableResult } from '../../store';
import { RestClientService } from '../rest-client.service';
import { InvitationsDataService, InvitedContent } from './invitations-data.service';

@Injectable()
export class ApiInvitationsDataService implements InvitationsDataService {
  constructor(private client: RestClientService) {}

  list(content: InvitedContent, page: number, size: number): ObservableResult<InvitationsPage> {
    return this.client.get<InvitationsPage>(this.url(content), { page: page.toString(), size: size.toString() }).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err: HttpErrorResponse) => ObservableResult.ofError(apiMessage(err))),
    );
  }

  invite(content: InvitedContent, request: { userUids: string[]; emails: string[] }): ObservableResult<InviteeResult[]> {
    return this.client.post<InviteeResult[]>(this.url(content), request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body ?? [])),
      catchError((err: HttpErrorResponse) => ObservableResult.ofError(apiMessage(err))),
    );
  }

  importCsv(content: InvitedContent, file: File): ObservableResult<InvitationImport> {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post<InvitationImport>(this.url(content, 'import'), formData).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err: HttpErrorResponse) => ObservableResult.ofError(apiMessage(err))),
    );
  }

  resend(content: InvitedContent, invitationUid: string): ObservableResult<void> {
    return this.client.post<void>(this.url(content, `${invitationUid}/resend`)).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError((err: HttpErrorResponse) => ObservableResult.ofError(apiMessage(err))),
    );
  }

  remove(content: InvitedContent, invitationUid: string): ObservableResult<void> {
    return this.client.delete<void>(this.url(content, invitationUid)).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError((err: HttpErrorResponse) => ObservableResult.ofError(apiMessage(err))),
    );
  }

  private url(content: InvitedContent, path?: string): string {
    const base = `${content.isCard ? 'cards' : 'playlists'}/${content.id}/invitations`;
    return Location.joinWithSlash(environment.apiRootUrl || '', path ? `${base}/${path}` : base);
  }
}

function apiMessage(err: HttpErrorResponse): string {
  return err?.status === 400 && err.error?.message ? err.error.message : '';
}
